import { Button, makeStyles, } from '@material-ui/core';
import { Close } from "@material-ui/icons";
import { Skeleton } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { list } from '../../../../../services/functionalities.service';
import { createGroup, updateGroup, verifyGroupMembers } from '../../../../../services/groups.service';
import DialogCustom from '../../../../Common/DialogCustom';
import AcessModelCard, { IAcessModelValues } from '../../components/Boxes/AccessModelCard';
import FuncionalityAcessCard, { IFunctionality } from '../../components/Boxes/FuncionalityAcessCard';
import GroupModalitiesCard from '../../components/Boxes/GroupModalitiesCard';
import ParticipantsCard from '../../components/Boxes/ParticipantsCard';
import { ActionButtonsContainer, CardColumn, CloseButton, Container, FormContent, Header, HeaderTitle } from './styles';

export interface IGroup {
    id: string; 
    name: string; 
    committeePackageName: string; 
    modalities: { id: string; name: string; }[];
    committeePackageId: string; 
    accessModel: {
      allResources: boolean;
      onlyRelatedToDirectory: boolean;
      onlyRelatedToModalities: boolean;
      onlyRelatedToOwner: boolean;
    };
    participants: { id: string; name: string; }[];
    selectedFunctionalities: string[];
};
  
interface IConfigureGroupModal {
    setModalStatus: (status: boolean) => void;
    handleCreateGroup: (id: string) => Promise<void>;
    packageCommitteeId: string;
    groupName: string;
    packageIsAdmin: boolean;
    adminPackage: boolean;
    groupToUpdate?: IGroup;
    setToastMessage: React.Dispatch<React.SetStateAction<{
        open: boolean;
        content: string;
        type: string;
        autoHideDuration: number;
    }>>;
};

export interface IFormattedFunctionality extends IFunctionality {
    hidden: boolean;
}

const useStyles = makeStyles((theme) => ({
    button: {
      ...theme.typography.subtitle1,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      textTransform: "capitalize",
    },
    buttonOutlined: {
      border: "1px solid",
      fontSize: "1rem",
      fontWeight: "bold",
      color: "#666666",
      textTransform: "capitalize",
    },
  }));
  
const ConfigureGroupModal: React.FC<IConfigureGroupModal> = ({
    setModalStatus,
    packageCommitteeId,
    groupName,
    packageIsAdmin,
    handleCreateGroup,
    adminPackage,
    groupToUpdate,
    setToastMessage
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const effectHasAlreadyRunned = useRef(false);

    const [acessModelValues, setAcessModelValues] = useState<IAcessModelValues>({
        allResources: true,
        onlyRelatedToDirectory: false,
        onlyRelatedToOwner: false,
        onlyRelatedToModalities: false,
    });    
    const [selectedModalities, setSelectedModalities] = useState<{id: string; name: string}[]>([])
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedFuncionalities, setSelectedFuncionalities] = useState<string[]>([]);
    const [participants, setParticipants] = useState<{ id: string; name: string; }[]>([]);
    const [functionalities, setFunctionalities] = useState<IFormattedFunctionality[]>([]);
    const [groupParticipantsArePartOfPackage, setGroupParticipantsArePartOfPackage] = useState<boolean>(false);

    const fetchFunctionalities = useCallback(async () => {
        setLoading(true);
        
        const { functionalities } = await list(adminPackage)

        const formattedFunctionalities = functionalities.map((functionality) => ({
            ...functionality,
            hidden: false,
        }));

        setFunctionalities(formattedFunctionalities);

        setLoading(false);
    }, [adminPackage]);
    
    useEffect(() => {        
        if (groupToUpdate) {
            setSelectedFuncionalities(groupToUpdate.selectedFunctionalities);
            setParticipants(groupToUpdate.participants);
            setAcessModelValues(groupToUpdate.accessModel);
            setSelectedModalities(groupToUpdate.modalities);
        };
        
        fetchFunctionalities();
        
        if (effectHasAlreadyRunned.current) {
            return;
        };        

        effectHasAlreadyRunned.current = true;
    }, [effectHasAlreadyRunned, fetchFunctionalities, groupToUpdate]);
    
    const saveNewGroup = useCallback(async() => {    
      try {
        const modalitiesIds = selectedModalities.map(modality => modality.id);

        await createGroup({
          name: groupName,
          committeePackageId: packageCommitteeId,
          functionalitiesIds: selectedFuncionalities,
          participantsIds: participants.map((participant) => participant.id),
          groupAdminLevel: adminPackage ? 2 : 3,
          modalitiesIds,
          accessModelAllContent: acessModelValues.allResources,
          accessModelModalityBased: acessModelValues.onlyRelatedToModalities,
          accessModelDirectoryBased: acessModelValues.onlyRelatedToDirectory,
          accessModelResponsibleBased: acessModelValues.onlyRelatedToOwner
        });
  
        handleCreateGroup(packageCommitteeId);
        setModalStatus(false);
      }catch(err: any) {
        const [ errorMessage ] = err?.response?.data?.errors || [t("softwareManagement.modules.groups.errorCreation")];
  
        setToastMessage({
          open: true,
          content: errorMessage || t("softwareManagement.modules.groups.errorCreation"),
          type: 'error',
          autoHideDuration: 4000,
        });
      }
    }, [acessModelValues, groupName, selectedModalities, packageCommitteeId, selectedFuncionalities, participants]);
    
    const handleUpdateGroup = useCallback(async() => {
      if(!groupToUpdate) return;
      const modalitiesIds = selectedModalities.map(modality => modality.id);
  
      try {
        await updateGroup({
          id: groupToUpdate.id,
          name: groupToUpdate.name,
          committeePackageId: packageCommitteeId,
          functionalitiesIds: selectedFuncionalities,
          modalitiesIds,
          participantsIds: participants.map((participant) => participant.id),
          accessModelAllContent: acessModelValues.allResources,
          accessModelDirectoryBased: acessModelValues.onlyRelatedToDirectory,
          accessModelResponsibleBased: acessModelValues.onlyRelatedToOwner,
          accessModelModalityBased: acessModelValues.onlyRelatedToModalities,         
        });
  
        setToastMessage({
          open: true,
          content: t("softwareManagement.modules.groups.successUpdate"),
          type: 'success',
          autoHideDuration: 4000,
        });
      }catch(err: any) {
        const [ errorMessage ] = err?.response?.data?.errors || [t("softwareManagement.modules.groups.errorUpdate")];
        
        setToastMessage({
          open: true,
          content: errorMessage || t("softwareManagement.modules.groups.errorUpdate"),
          type: 'error',
          autoHideDuration: 4000,
        });
      }
    }, [acessModelValues, groupName, selectedModalities, packageCommitteeId, groupToUpdate, participants, selectedFuncionalities]);

    const handleTriggerSubmition = useCallback(async() => {
        if(groupToUpdate) {
            handleUpdateGroup();
        }else {
            saveNewGroup();
        };
    }, [handleUpdateGroup, saveNewGroup]);        

    const handleSave = useCallback(async() => {
        const areMembersPartOfOthePackageGroup = await verifyGroupMembers({
            committeePackageId: packageCommitteeId,
            groupId: groupToUpdate?.id,
            participantsIds: participants.map((participant) => participant.id),
        });

        if(areMembersPartOfOthePackageGroup) {
            setGroupParticipantsArePartOfPackage(true);
         
            return;
        }

        handleTriggerSubmition();
    }, [packageCommitteeId, handleTriggerSubmition, participants]);
    
    return (
        <Container>
            <DialogCustom
                open={groupParticipantsArePartOfPackage}
                handleClose={() => setGroupParticipantsArePartOfPackage(false)}
                handleChange={handleTriggerSubmition}
                title={t(`forms.dialog.alert`)}
                body={t("softwareManagement.modules.groups.alerts.userIsPartOfAnotherPackageGroup")}
                confirmText={t(`forms.dialog.yes`)}
                cancelText={t(`forms.dialog.no`)}
                alert={true}
            />
            <Header>
                <HeaderTitle>
                    {`Configuraçāo de grupo ${groupToUpdate?.name || groupName}`}
                </HeaderTitle>

                <CloseButton onClick={() => setModalStatus(false)}>
                    <Close />
                </CloseButton>
            </Header>

            <FormContent>            
                <CardColumn>
                    {loading ? (
                        <>
                            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '20%' }}/>
                            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '80%' }}/>
                        </>
                    ) : (
                        <FuncionalityAcessCard 
                            selectedPackageIsAdmin={packageIsAdmin}
                            selectedFuncionalities={selectedFuncionalities}
                            setSelectedFuncionalities={setSelectedFuncionalities}
                            functionalities={functionalities}
                            setFunctionalities={setFunctionalities}
                        />
                    )}
                </CardColumn>

                <CardColumn>
                    {loading ? (
                        <>
                            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '40%' }}/>
                            <Skeleton variant={"rectangular"} style={{ borderRadius: '8px', width: '100%', height: '60%' }}/>
                        </>
                    ) : (
                        <>
                            <AcessModelCard 
                                acessModelValues={acessModelValues}
                                setAcessModelValues={setAcessModelValues}
                            />
                            
                            {acessModelValues.onlyRelatedToModalities && (
                                <GroupModalitiesCard 
                                    selectedModalities={selectedModalities}
                                    setSelectedModalities={setSelectedModalities}
                                />
                            )}

                            <ParticipantsCard participants={participants} setParticipants={setParticipants} />
                        </>
                    )}
                </CardColumn>
            </FormContent>

            <ActionButtonsContainer>
                <Button
                    variant="outlined"
                    className={classes.buttonOutlined}
                    onClick={() => setModalStatus(false)}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    className={classes.button}
                    onClick={handleSave}
                >
                    Salvar
                </Button>
            </ActionButtonsContainer>
        </Container>
    )
}

export default ConfigureGroupModal;